import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { AudioPlayer } from './AudioPlayer.js';
import { cn } from './cn.js';
import { Button, buttonClasses } from './Button.js';
import { Icon, type IconName } from './Icon.js';
import { Modal } from './Modal.js';
import { contentFromDataUrl, formatBytes, MAX_BYTES } from './DocumentCapture.js';
import {
  acceptAttribute,
  acceptedLabels,
  acceptedSentence,
  classifyAttachment,
  kindOfType,
  MAX_ATTACHMENTS,
  type AttachmentKind,
} from './files.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from './primitives/index.js';

/**
 * O anexo da casa: a grade, a prévia, o visualizador e o campo que recebe.
 *
 * ## Por que ele existe
 *
 * Anexo aparecia em quatro telas e tinha **quatro anatomias**. O compositor da
 * demanda listava nome e tamanho numa linha com um `x`; a aba "Documentos" da
 * mesma tela desenhava outra linha, com ícone, autor e um botão "Baixar" que
 * virava link depois da resposta; a mensagem na linha do tempo mostrava uma
 * pastilha com o nome e nada mais; e o portal mostrava o nome com uma frase
 * dizendo que ele não abre. Nenhuma das quatro mostrava **o arquivo**.
 *
 * O que se perde com isso não é estética. Quem responde uma demanda com três
 * capturas de tela anexadas precisa saber qual é qual antes de abrir as três, e
 * `Screenshot 2026-09-16 163850.png` não diz. A prévia é a única coisa que diz.
 *
 * ## O que ele NÃO faz, e é decisão
 *
 * - **Não comprime nada.** É a mesma razão de `files.ts`: o anexo mais comum é
 *   a captura de tela do erro, e passá-la pelo canvas borra exatamente o texto
 *   que a pessoa está relatando. Quem captura documento fotografado continua
 *   usando a `DocumentCapture`, que comprime de propósito e conhece menos
 *   formatos porque é outro gesto.
 * - **Não anexa por endereço.** O "Colar URL" do desenho de referência pede
 *   coluna nova em `tickets_attachments` — o anexo passaria a ser
 *   ou-arquivo-ou-endereço —, rota, contrato e migration, e abre a pergunta de
 *   buscar uma URL de fora pelo servidor. Fica de fora inteiro (regra 14) em
 *   vez de virar um botão que abre um campo que não grava.
 * - **Não sabe enviar.** Ele mostra e escolhe; quem sobe é a tela, porque a
 *   rota, a ordem das chamadas e o que fazer com o envio parcial são do módulo.
 */

/**
 * Um anexo na grade — o que já subiu e o que ainda vai subir, no mesmo formato.
 *
 * `url` e `resolveUrl` são as duas maneiras de chegar ao conteúdo, e existem as
 * duas porque as duas situações são diferentes:
 *
 * - o arquivo **recém-escolhido** tem uma `blob:` URL de graça, criada na
 *   escolha;
 * - o arquivo **já gravado** abre por URL assinada de curta duração, e assiná-la
 *   é uma ida ao servidor. `resolveUrl` é uma função por isso: ela é chamada uma
 *   vez por ladrilho, e só para o que dá para pré-visualizar — pedir a URL
 *   assinada de um `.docx` é uma requisição para mostrar o mesmo ícone que já
 *   estava lá.
 */
export interface AttachmentItem {
  /**
   * A chave da lista e da remoção.
   *
   * Não é o nome: dois arquivos com o mesmo nome e o mesmo tamanho são um caso
   * normal — duas capturas de tela seguidas —, e usar o nome faria a segunda
   * sumir da grade.
   */
  key: string;
  nome: string;
  typeMime: string;
  sizeBytes: number;
  /** A fonte da prévia, quando já se sabe qual é. */
  url?: string;
  /** A fonte da prévia, quando obtê-la custa uma requisição. */
  resolveUrl?: () => Promise<string>;
  /** Uma segunda linha sob o nome — "por Fulano · 16/09", por exemplo. */
  detail?: string;
}

/** Um arquivo escolhido na tela, com a `blob:` URL que desenha a prévia dele. */
export interface LocalAttachment extends AttachmentItem {
  file: File;
  /** O tipo RESOLVIDO pela allowlist, nunca o palpite do aparelho. */
  typeMime: string;
  url: string;
}

/**
 * O conteúdo do arquivo em base64, sem o prefixo `data:` — o que as rotas pedem.
 *
 * Morava no `TicketScreen.tsx` e subiu porque a segunda tela que anexar vai
 * precisar da mesma função, e a versão dela erraria o mesmo detalhe: o
 * `readAsDataURL` devolve `data:<tipo>;base64,<conteúdo>`, e mandar a string
 * inteira grava um arquivo que começa com o próprio cabeçalho.
 */
export function readAttachmentContent(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Não foi possível ler ${file.name}.`));
    reader.onload = () => resolve(contentFromDataUrl(String(reader.result)));
    reader.readAsDataURL(file);
  });
}

/**
 * A lista com um item mudado de lugar.
 *
 * Fora do componente porque é a única parte da reordenação que dá para cobrar
 * num teste: `splice` duas vezes tem um caso que a intuição erra — mover para
 * frente tira o item antes de inserir, e o índice de destino já andou um.
 */
export function reordered<T>(list: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) {
    return list;
  }
  const out = [...list];
  const [moved] = out.splice(from, 1);
  out.splice(to, 0, moved!);
  return out;
}

/**
 * A lista de arquivos escolhidos, com as `blob:` URLs cuidadas.
 *
 * A URL de objeto é memória do documento e **não é liberada sozinha**: uma aba
 * que fica aberta a manhã inteira numa mesa de demandas, com dez anexos
 * escolhidos e descartados por demanda, segura dez vídeos de 20 MB até alguém
 * recarregar a página. Quem esquece de revogar não vê nada errado na tela — é o
 * motivo de a criação e a revogação morarem juntas, aqui, em vez de em cada uma
 * das telas que anexam.
 */
export function useAttachments(): {
  items: LocalAttachment[];
  add: (files: File[], typesMime: string[]) => void;
  remove: (key: string) => void;
  move: (from: number, to: number) => void;
  clear: () => void;
} {
  const [items, setItems] = useState<LocalAttachment[]>([]);
  const nextKey = useRef(0);
  /*
   * As URLs vivas, num ref. O efeito de limpeza do desmonte roda uma vez, com o
   * que ele capturou na criação — se ele lesse o estado, liberaria a lista do
   * primeiro render e deixaria escapar tudo o que foi escolhido depois.
   */
  const liveUrls = useRef(new Set<string>());

  useEffect(
    () => () => {
      for (const url of liveUrls.current) URL.revokeObjectURL(url);
      liveUrls.current.clear();
    },
    [],
  );

  const add = useCallback((files: File[], typesMime: string[]) => {
    const added = files.map((file, i) => {
      const url = URL.createObjectURL(file);
      liveUrls.current.add(url);
      return {
        key: `local-${nextKey.current++}`,
        nome: file.name,
        typeMime: typesMime[i] ?? file.type,
        sizeBytes: file.size,
        url,
        file,
      };
    });
    setItems((before) => [...before, ...added]);
  }, []);

  const remove = useCallback((key: string) => {
    setItems((before) => {
      const leaving = before.find((i) => i.key === key);
      if (leaving) {
        URL.revokeObjectURL(leaving.url);
        liveUrls.current.delete(leaving.url);
      }
      return before.filter((i) => i.key !== key);
    });
  }, []);

  const move = useCallback((from: number, to: number) => {
    setItems((before) => reordered(before, from, to));
  }, []);

  const clear = useCallback(() => {
    setItems((before) => {
      for (const i of before) {
        URL.revokeObjectURL(i.url);
        liveUrls.current.delete(i.url);
      }
      return [];
    });
  }, []);

  return { items, add, remove, move, clear };
}

/**
 * A fonte do ladrilho, resolvida uma vez — e só quando há o que desenhar.
 */
function useSource(item: AttachmentItem | undefined, wanted: boolean): string | null {
  const [url, setUrl] = useState<string | null>(item?.url ?? null);
  const resolve = item?.resolveUrl;
  const ready = item?.url;

  useEffect(() => {
    if (ready) {
      setUrl(ready);
      return;
    }
    if (!wanted || !resolve) return;

    let alive = true;
    void resolve()
      .then((r) => {
        if (alive) setUrl(r);
      })
      /*
       * O silêncio é deliberado: a prévia é conveniência, e a falha dela já
       * aparece como o ladrilho do gênero — o mesmo desenho de antes de ela
       * carregar. Quem precisa do arquivo clica, e aí o erro tem onde aparecer.
       */
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [ready, resolve, wanted]);

  return url;
}

/*
 * As chaves são os VALORES de `AttachmentKind` e os valores são os de
 * `IconName` — os dois são uniões literais, e traduzir qualquer um dos lados
 * quebra a indexação.
 */
// @valor
const GENRE_ICON: Record<AttachmentKind, IconName> = {
  image: 'imagem',
  video: 'video',
  audio: 'audio',
  pdf: 'documento',
  document: 'documento',
};

/** O ladrilho do gênero — o que aparece quando não há prévia, e não um vazio. */
function GenreMark({ kind }: { kind: AttachmentKind }) {
  return (
    <span
      aria-hidden
      className="grid size-full place-items-center bg-superficie-suave text-texto-tenue"
    >
      <Icon nome={GENRE_ICON[kind]} size={24} />
    </span>
  );
}

/** O que cabe dentro do quadrado, por gênero. */
function Thumb({ item, kind }: { item: AttachmentItem; kind: AttachmentKind }) {
  const previewable = kind === 'image' || kind === 'video' || kind === 'pdf';
  const url = useSource(item, previewable);
  const [broke, setBroke] = useState(false);

  if (!url || broke) return <GenreMark kind={kind} />;

  if (kind === 'image') {
    return (
      <img
        src={url}
        alt=""
        loading="lazy"
        className="size-full object-cover"
        onError={() => setBroke(true)}
      />
    );
  }

  if (kind === 'video') {
    return (
      <>
        {/*
         * `#t=0.1` não é superstição: sem o fragmento, o Chrome baixa os
         * metadados e desenha um retângulo preto — o primeiro quadro só é
         * decodificado quando há um instante pedido. E `muted` porque um
         * `<video>` com som pode ser barrado pela política de reprodução
         * automática antes mesmo de mostrar o quadro.
         */}
        <video
          src={`${url}#t=0.1`}
          preload="metadata"
          muted
          playsInline
          className="size-full object-cover"
          onError={() => setBroke(true)}
        />
        <span
          aria-hidden
          className="absolute inset-0 grid place-items-center text-texto-inverso"
        >
          <span className="grid size-10 place-items-center rounded-pilula bg-veu">
            <Icon nome="tocar" size={20} />
          </span>
        </span>
      </>
    );
  }

  /*
   * O PDF é o ÚLTIMO caso, e a saída precisa ser explícita.
   *
   * Aqui havia um `return` sem guarda, e áudio e documento caíam nele. Medido
   * em 17/09: anexar um MP3 tocava o arquivo sozinho, alto, no instante em que
   * o ladrilho aparecia — o Chrome instancia o blob dentro do `<object>`,
   * descobre que é áudio, monta um player e começa a tocar.
   *
   * O `previewable` lá em cima não protegia: ele decide se vale PEDIR a URL
   * assinada, e um arquivo recém-escolhido já tem a `blob:` dele de graça. A
   * pergunta "eu sei desenhar isto?" é esta linha, e ela não existia.
   */
  if (kind !== 'pdf') return <GenreMark kind={kind} />;

  /*
   * `pointer-events-none` no embrulho porque o `<object>` engole o clique, e o
   * clique aqui pertence ao botão que abre o visualizador — sem isso o ladrilho
   * do PDF seria o único da grade que não abre ao ser clicado.
   */
  return (
    <span aria-hidden className="pointer-events-none block size-full bg-superficie">
      <object
        data={`${url}#toolbar=0&navpanes=0&view=FitH`}
        type="application/pdf"
        className="size-full"
      >
        <GenreMark kind="pdf" />
      </object>
    </span>
  );
}

interface DragHandle {
  index: number;
  total: number;
  onMove: (from: number, to: number) => void;
  onDragStart: (index: number) => void;
  onDropAt: (index: number) => void;
}

function Tile({
  item,
  onOpen,
  onRemove,
  drag,
  disabled,
  compact = false,
}: {
  item: AttachmentItem;
  /**
   * Ausente, o ladrilho não abre — e nem finge que abre.
   *
   * É o caso do portal: o associado vê que o anexo existe e não tem por onde
   * baixá-lo, porque a rota que o entregaria com `ctx.member.id` no `where`
   * ainda não nasceu. Um botão ali abriria um visualizador preso em "Abrindo o
   * arquivo…" para sempre — a tela afirmando o que o código não cumpre.
   */
  onOpen?: () => void;
  onRemove?: () => void;
  /** Ausente, o ladrilho não arrasta nem responde às setas. */
  drag?: DragHandle;
  disabled?: boolean;
  /** 80px, e não 144: a faixa do compositor de resposta — ver `AttachmentStrip`. */
  compact?: boolean;
}) {
  const kind = kindOfType(item.typeMime);
  const frameClasses =
    'relative block aspect-square w-full overflow-hidden rounded-borda border border-borda bg-superficie-suave';

  function byKeyboard(event: KeyboardEvent<HTMLButtonElement>) {
    if (!drag) return;
    const delta = event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0;
    if (delta === 0) return;
    event.preventDefault();
    const to = drag.index + delta;
    if (to < 0 || to >= drag.total) return;
    drag.onMove(drag.index, to);
  }

  return (
    <li
      className={cn('group relative shrink-0', compact ? 'w-20' : 'w-36')}
      draggable={drag !== undefined && !disabled}
      onDragStart={() => drag?.onDragStart(drag.index)}
      onDragOver={(e: DragEvent) => {
        // Só a reordenação: arquivo vindo de fora é assunto da zona de queda.
        if (drag && !e.dataTransfer.types.includes('Files')) e.preventDefault();
      }}
      onDrop={(e: DragEvent) => {
        if (!drag || e.dataTransfer.types.includes('Files')) return;
        e.preventDefault();
        e.stopPropagation();
        drag.onDropAt(drag.index);
      }}
    >
      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          aria-label={`Abrir ${item.nome}`}
          className={cn(frameClasses, 'transition-transform active:scale-[0.98]')}
        >
          <Thumb item={item} kind={kind} />
          {/*
           * A sobreposição aparece no `hover` E no `focus-within` do ladrilho.
           * Só no `hover`, quem chega pelo Tab vê o foco pousar num botão que o
           * desenho mantém invisível — e a lixeira ao lado some junto.
           */}
          <span
            aria-hidden
            className="absolute inset-0 grid place-items-center bg-veu text-texto-inverso opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
          >
            <Icon nome="expandir" size={24} />
          </span>
        </button>
      ) : (
        <span className={frameClasses}>
          <Thumb item={item} kind={kind} />
        </span>
      )}

      {(onRemove ?? drag) && (
        <span className="absolute top-1.5 right-1.5 flex flex-col gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
          {onRemove && (
            <button
              type="button"
              disabled={disabled}
              onClick={onRemove}
              aria-label={`Remover ${item.nome}`}
              className="grid size-8 place-items-center rounded-pequeno bg-erro text-texto-inverso hover:bg-erro-forte active:bg-erro-forte disabled:opacity-60"
            >
              <Icon nome="lixeira" size={16} />
            </button>
          )}
          {drag && (
            /*
             * A alça arrasta com o ponteiro e anda com as setas, e as duas
             * coisas precisam existir: arrastar é o gesto do desenho, e ele não
             * tem equivalente no teclado nem no leitor de tela. O rótulo diz a
             * posição porque "mover" sozinho não responde "de onde para onde".
             */
            <button
              type="button"
              disabled={disabled}
              onKeyDown={byKeyboard}
              aria-label={`Mover ${item.nome} — ${drag.index + 1} de ${drag.total}, use as setas`}
              className="grid size-8 cursor-grab place-items-center rounded-pequeno bg-texto-suave text-texto-inverso hover:bg-texto active:cursor-grabbing disabled:opacity-60"
            >
              <Icon nome="mover" size={16} />
            </button>
          )}
        </span>
      )}

      <span className="mt-2 block truncate text-center text-xs text-texto-suave transition-colors group-hover:text-primaria-texto">
        {item.nome}
      </span>
      {item.detail && (
        <span className="mt-0.5 block truncate text-center text-[0.6875rem] text-texto-tenue">
          {item.detail}
        </span>
      )}
    </li>
  );
}

function Arrow({
  direction,
  disabled,
  onClick,
}: {
  direction: 'esquerda' | 'direita';
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={direction === 'esquerda' ? 'Anexo anterior' : 'Próximo anexo'}
      /*
       * As setas moram DENTRO do véu, e herdavam o defeito do nome do arquivo:
       * `texto-inverso` some no tema escuro (1,14:1 contra o véu composto) e
       * `primaria` no ponteiro some no claro (1,30:1).
       *
       * O ponteiro clareia, e não colore. Medido em 18/09: o laranja do chrome
       * dá **2,46:1** contra o véu claro — contra um cinza médio nenhuma cor
       * saturada chega aos 4,5. O degrau que funciona é de luminosidade:
       * repouso em `chrome-texto-suave` (4,88:1 no claro, 6,25:1 no escuro) e
       * ponteiro em `chrome-texto` (6,45:1 e 18,04:1), que é mais claro que o
       * repouso — um hover que ESCURECE comunica o contrário do que faz.
       */
      className="grid size-11 place-items-center rounded-pilula text-chrome-texto-suave hover:text-chrome-texto active:opacity-70 disabled:invisible"
    >
      <Icon nome={`seta-${direction}`} size={24} />
    </button>
  );
}

/**
 * O que se diz de um `.docx` — e é uma frase, não um quadrado esperando.
 *
 * Nenhum navegador desenha um documento do Office sem um conversor do lado do
 * servidor, e a casa não tem um. A tela diz isso em vez de mostrar uma prévia em
 * branco que a pessoa fica olhando.
 */
function NoPreview({ item }: { item: AttachmentItem }) {
  return (
    <div className="w-full max-w-sm rounded-grande border border-borda bg-superficie p-6 text-center">
      <span
        aria-hidden
        className="mx-auto grid size-14 place-items-center rounded-pilula bg-superficie-suave text-texto-suave"
      >
        <Icon nome="documento" size={24} />
      </span>
      <p className="mt-3 truncate text-sm font-semibold">{item.nome}</p>
      <p className="mt-1 text-xs text-texto-suave">
        {formatBytes(item.sizeBytes)} · este formato não tem prévia no navegador. Baixe
        para abrir.
      </p>
    </div>
  );
}

/** O anexo aberto, por gênero. */
function Content({
  item,
  kind,
  url,
}: {
  item: AttachmentItem;
  kind: AttachmentKind;
  url: string;
}) {
  if (kind === 'image') {
    return (
      <img src={url} alt={item.nome} className="max-h-full max-w-full object-contain" />
    );
  }

  if (kind === 'video') {
    return (
      <video
        src={url}
        controls
        autoPlay
        className="max-h-full max-w-full object-contain"
        aria-label={item.nome}
      />
    );
  }

  if (kind === 'audio') {
    /*
     * O player é o `AudioPlayer`, e o cartão em volta é o que ele NÃO faz.
     *
     * Aqui havia o player nativo do navegador, com o argumento de que ele já
     * traz progresso arrastável, volume, tempo e teclado. Traz — em três
     * desenhos diferentes, nenhum deles com os tokens da marca e nenhum deles
     * escurecendo com o tema. O que continua valendo daquela decisão é o
     * contexto: uma barra solta no meio de uma sobreposição preta é um controle
     * sem dono, e o nome e o tamanho do arquivo são o que lhe dá um.
     */
    return (
      <div className="w-full max-w-lg rounded-grande border border-borda bg-superficie p-5">
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            className="grid size-11 shrink-0 place-items-center rounded-pilula bg-primaria-suave text-primaria-texto"
          >
            <Icon nome="audio" size={24} />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{item.nome}</p>
            <p className="text-xs text-texto-suave">{formatBytes(item.sizeBytes)}</p>
          </div>
        </div>
        <AudioPlayer src={url} label={item.nome} className="mt-4" />
      </div>
    );
  }

  if (kind === 'pdf') {
    return (
      <object
        data={url}
        type="application/pdf"
        className="h-full w-full"
        title={item.nome}
      >
        <NoPreview item={item} />
      </object>
    );
  }

  return <NoPreview item={item} />;
}

/**
 * O visualizador — uma sobreposição por cima de tudo, com o anexo dentro.
 *
 * Ele é um `Dialog` de verdade e não uma `<div>` posicionada: foco preso,
 * `Escape` que fecha, foco devolvido a quem abriu e o resto da página escondido
 * do leitor de tela são o que separa "abriu uma imagem" de "a página sumiu e o
 * Tab está navegando por baixo dela". As setas do teclado andam entre os anexos,
 * e é por isso que ele recebe a lista inteira e um índice, e não um item só.
 */
function Viewer({
  items,
  index,
  onIndex,
  onClose,
}: {
  items: AttachmentItem[];
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
}) {
  const item = items[index];
  const url = useSource(item, true);

  useEffect(() => {
    function onKey(event: globalThis.KeyboardEvent) {
      if (event.key === 'ArrowLeft' && index > 0) onIndex(index - 1);
      if (event.key === 'ArrowRight' && index < items.length - 1) onIndex(index + 1);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [index, items.length, onIndex]);

  if (!item) return null;
  const kind = kindOfType(item.typeMime);

  return (
    <Dialog open onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        showCloseButton={false}
        className="top-0 left-0 h-dvh max-h-none w-screen max-w-none translate-x-0 translate-y-0 gap-0 rounded-none border-0 bg-transparent p-0 shadow-none sm:max-w-none"
      >
        <DialogTitle className="sr-only">{item.nome}</DialogTitle>
        <DialogDescription className="sr-only">
          {`Anexo ${index + 1} de ${items.length}, ${formatBytes(item.sizeBytes)}.`}
        </DialogDescription>

        {/*
         * `h-dvh w-screen`, e não `size-full`.
         *
         * `size-full` é `height: 100%`, e porcentagem só resolve contra um pai
         * de altura DEFINIDA. O pai aqui é o `DialogContent` do Radix, que é
         * `grid` de linha automática: a altura dele é a do conteúdo, a
         * porcentagem caía para `auto`, e com ela caíam o `minmax(0,1fr)` da
         * linha do meio e o `max-h-full` da mídia lá dentro.
         *
         * A imagem escondia o defeito porque `max-w-full` a segurava pela
         * largura. O vídeo não: um arquivo de celular em retrato saía com a
         * altura intrínseca, transbordava a janela e não havia o que rolar —
         * ele ficava visível pela metade, sem barra de rolagem em lugar nenhum.
         * Com a unidade de viewport a cadeia toda volta a ter altura definida.
         */}
        {/*
         * `data-chrome`: o véu é um BLOCO TINGIDO, e é o único do produto que
         * nunca foi medido.
         *
         * `theme.test.ts` mede 40 combinações e nenhuma envolve `veu`, porque
         * ele é o único token com alfa — a conta do teste é sobre cor opaca, e
         * a superfície real só existe depois de o navegador compor. Medido no
         * navegador em 18/09, com os DOIS véus que esta tela empilha (o overlay
         * do Radix e o desta grade), o composto é `rgb(81, 96, 112)` no tema
         * claro e `rgb(0, 1, 3)` no escuro.
         *
         * Contra os 81/96/112 do claro, o botão de contorno que estava aqui dava
         * **1,30:1** de borda e **1,10:1** de rótulo: ele não ficava fraco, ele
         * não era desenhado. E trocar a variante não conserta — o preenchimento
         * do primário dá os MESMOS 1,30:1, porque o laranja da marca tem a
         * luminância do véu claro. É letra por letra o defeito que a inversão
         * de bloco tingido nasceu para consertar no cabeçalho do portal.
         *
         * Com a marca, a inversão do `theme.css` pega: preenchimento a 6,45:1
         * contra o véu e rótulo a 11,70:1 contra o preenchimento no claro,
         * 18,04:1 e 16,45:1 no escuro. Ela também troca o anel de foco para o
         * laranja claro, que é o que um refletor escuro pede.
         */}
        <div
          data-chrome
          className="grid h-dvh w-screen grid-rows-[auto_minmax(0,1fr)_auto] bg-veu"
        >
          <div className="flex items-center gap-3 p-3">
            <span className="shrink-0 rounded-borda bg-superficie px-2.5 py-1 text-sm font-semibold">
              {index + 1} / {items.length}
            </span>
            {/*
             * `chrome-texto`, e não `texto-inverso` — este era o SEGUNDO defeito
             * desta tela, e ele some só no tema escuro.
             *
             * `texto-inverso` quer dizer "texto sobre um preenchimento da
             * paleta", e por isso ele é branco no claro e navy ESCURO no escuro.
             * Sobre um véu que compõe `rgb(0, 1, 3)`, o nome do arquivo que a
             * pessoa está olhando dava **1,14:1**: ele estava lá, com o tamanho
             * certo e o peso certo, e não se via. No claro o mesmo token dá
             * 6,45:1, e é por isso que ninguém topou com isso.
             *
             * `chrome-texto` é claro nos DOIS temas, que é o que uma superfície
             * escura pede — 6,45:1 no claro e 18,04:1 no escuro.
             */}
            <span className="min-w-0 flex-1 truncate text-center text-sm font-medium text-chrome-texto">
              {item.nome}
            </span>
            {/*
             * Primária, e não contorno: fechar o refletor é um dos dois atos
             * desta tela, e ela não tem uma ação-chave à qual esta seja
             * alternativa. As duas ficam em cantos opostos e não dividem
             * fileira, então a régua de "uma primária por tela" — que existe
             * para impedir uma barra de cinco botões preenchidos competindo —
             * não é o que está em jogo aqui.
             */}
            <Button icon="fechar" onClick={onClose}>
              Fechar
            </Button>
          </div>

          <div className="grid min-h-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 px-2">
            <Arrow
              direction="esquerda"
              disabled={index === 0}
              onClick={() => onIndex(index - 1)}
            />
            {/*
             * `overflow-auto` é a rede de segurança: o `max-h-full` da mídia já
             * a faz caber, e o que ele não alcança — um PDF que o navegador
             * desenha na altura dele, uma imagem que falhou e virou cartão —
             * passa a ter por onde rolar em vez de ficar cortado.
             */}
            <div className="grid size-full min-h-0 place-items-center overflow-auto">
              {url ? (
                <Content item={item} kind={kind} url={url} />
              ) : (
                <p className="text-sm text-chrome-texto">Abrindo o arquivo…</p>
              )}
            </div>
            <Arrow
              direction="direita"
              disabled={index === items.length - 1}
              onClick={() => onIndex(index + 1)}
            />
          </div>

          <div className="flex items-center justify-center p-3">
            {/*
             * "Abrir em nova aba" e não "Baixar": a URL assinada é a mesma nos
             * dois casos, e a aba resolve os dois — o navegador mostra o que
             * sabe mostrar e baixa o que não sabe. Um segundo botão prometeria
             * uma diferença que não existe.
             */}
            {url && (
              <a href={url} target="_blank" rel="noreferrer" className={buttonClasses()}>
                <Icon nome="expandir" size={16} />
                Abrir em nova aba
              </a>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A grade, e o visualizador que ela abre. É a peça de LEITURA — sem envio.
 *
 * Serve a mensagem da linha do tempo, a aba "Documentos" e o portal, que antes
 * desenhavam três coisas diferentes para o mesmo dado.
 */
export function AttachmentGallery({
  items,
  label,
  empty,
  className,
}: {
  items: AttachmentItem[];
  label?: ReactNode;
  /** O que dizer quando não há nenhum. Ausente, a grade não desenha nada. */
  empty?: ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState<number | null>(null);

  if (items.length === 0) return empty ? <>{empty}</> : null;

  return (
    <div className={className}>
      {label && <p className="mb-2 text-sm font-medium text-texto">{label}</p>}
      <ul className="flex flex-wrap gap-4">
        {items.map((item, i) => (
          <Tile
            key={item.key}
            item={item}
            /*
             * Quem decide se abre é o DADO, e não uma prop que a tela pode
             * esquecer de passar: um anexo sem `url` e sem `resolveUrl` não tem
             * de onde sair, e o ladrilho dele fica inerte por construção.
             */
            onOpen={(item.url ?? item.resolveUrl) ? () => setOpen(i) : undefined}
          />
        ))}
      </ul>
      {open !== null && (
        <Viewer
          items={items}
          index={open}
          onIndex={setOpen}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}

/**
 * O campo que recebe anexos: arrastar, escolher, ver, reordenar e remover.
 *
 * A classificação acontece **aqui**, na escolha, e não no envio — um vídeo de 40
 * MB recusado depois de dois minutos de upload numa rede móvel é o defeito que
 * `classifyAttachment` existe para não deixar acontecer. `onAdd` recebe só o que
 * passou, com o tipo já resolvido pela allowlist; o que não passou vira o
 * diálogo "Formato não aceito", com o nome de cada arquivo e o motivo.
 */
export function AttachmentField({
  label = 'Anexos',
  items,
  onAdd,
  onRemove,
  onReorder,
  max = MAX_ATTACHMENTS,
  disabled = false,
  busy = false,
  restrict,
  prompt,
}: {
  label?: ReactNode;
  items: AttachmentItem[];
  /** Os aceitos, e o tipo resolvido de cada um, na mesma ordem. */
  onAdd: (files: File[], typesMime: string[]) => void;
  onRemove: (key: string) => void;
  /** Ausente, o ladrilho não arrasta — a ordem passa a ser a da escolha. */
  onReorder?: (from: number, to: number) => void;
  max?: number;
  disabled?: boolean;
  /** "Lendo…" no gatilho, enquanto a tela digere o que foi escolhido. */
  busy?: boolean;
  /** Aceitar menos que a casa — ver `AttachmentRestriction`. */
  restrict?: AttachmentRestriction;
  /**
   * A área vazia INTEIRA vira o alvo do clique, com esta frase no lugar de
   * "Arraste um arquivo ou [Buscar arquivo]" — "Clique ou arraste o PDF", com
   * o limite embaixo (28/10/2026, o envio da nota no Início do Comercial).
   * Com arquivo escolhido, o campo volta ao desenho de sempre.
   */
  prompt?: { title: string; hint?: string };
}) {
  const idInput = useId();
  const [over, setOver] = useState(false);
  const [open, setOpen] = useState<number | null>(null);
  const from = useRef<number | null>(null);

  const full = items.length >= max;
  const accept = acceptAttribute(restrict?.accept);

  /* a recepção é a MESMA do compositor de resposta — `useAttachmentIntake` */
  const intake = useAttachmentIntake({
    count: items.length,
    onAdd,
    max,
    disabled,
    restrict,
  });
  const receive = intake.receive;

  /* O seletor nativo, o mesmo nos dois desenhos — ver o comentário do gatilho. */
  const fileInput = (
    <input
      id={idInput}
      type="file"
      multiple
      accept={accept}
      aria-label="Buscar arquivo para anexar"
      aria-busy={busy || undefined}
      disabled={disabled || busy || full}
      onChange={(e) => {
        const entry = e.target;
        const chosen = [...(entry.files ?? [])];
        // Sem isto, escolher o MESMO arquivo de novo não dispara `change` — e
        // quem removeu por engano fica sem conseguir escolhê-lo outra vez.
        entry.value = '';
        receive(chosen);
      }}
      className="absolute inset-0 size-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
    />
  );

  const trigger =
    (
      /*
       * O `<input type="file">` sobreposto ao gatilho, e não escondido com um
       * `.click()` no JavaScript: assim o clique, o toque, o Tab e o Enter são os
       * nativos. É a mesma anatomia da `DocumentCapture`, e o motivo de o gatilho
       * ser um `<span>` e não um `Button` é que um `<button>` por cima de um input
       * faria o clique dar a volta pelo JavaScript para chegar ao mesmo lugar.
       */
      <span className={buttonClasses({ variant: 'contorno', className: 'relative' })}>
        {fileInput}
        <Icon nome="busca" size={16} />
        {busy ? 'Lendo…' : 'Buscar arquivo'}
      </span>
    );

  return (
    <div>
      <p className="mb-1.5 text-sm font-medium text-texto">{label}</p>

      <div
        onDragOver={(e: DragEvent<HTMLDivElement>) => {
          // Sem a conferência, arrastar um LADRILHO por cima da moldura acende
          // a zona de queda — a tela promete receber o que ela vai ignorar.
          if (!e.dataTransfer.types.includes('Files')) return;
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e: DragEvent<HTMLDivElement>) => {
          if (!e.dataTransfer.types.includes('Files')) return;
          e.preventDefault();
          setOver(false);
          if (!disabled) receive([...e.dataTransfer.files]);
        }}
        className={cn(
          'rounded-grande border border-dashed p-4 transition-colors',
          over
            ? 'border-primaria-texto bg-primaria-suave'
            : 'border-borda-forte bg-superficie',
        )}
      >
        {items.length === 0 && prompt ? (
          <div className="relative flex flex-col items-center gap-2 py-6 text-center">
            {fileInput}
            <span className="grid size-10 place-items-center rounded-full bg-superficie-suave text-texto-suave">
              <Icon nome="nuvem" size={20} />
            </span>
            <p className="text-sm font-semibold text-primaria-texto">
              {busy ? 'Lendo…' : prompt.title}
            </p>
            {prompt.hint && <p className="rotulo text-texto-suave">{prompt.hint}</p>}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <Icon nome="nuvem" size={24} className="text-texto-tenue" />
            <p className="text-sm text-texto-suave">Arraste um arquivo ou</p>
            {trigger}
          </div>
        ) : (
          <ul className="flex flex-wrap items-start gap-4">
            {items.map((item, i) => (
              <Tile
                key={item.key}
                item={item}
                disabled={disabled}
                onOpen={() => setOpen(i)}
                onRemove={disabled ? undefined : () => onRemove(item.key)}
                drag={
                  onReorder && items.length > 1
                    ? {
                        index: i,
                        total: items.length,
                        onMove: onReorder,
                        onDragStart: (index) => {
                          from.current = index;
                        },
                        onDropAt: (index) => {
                          if (from.current !== null) onReorder(from.current, index);
                          from.current = null;
                        },
                      }
                    : undefined
                }
              />
            ))}
            {/* Desabilitado (só leitura), o convite a anexar não aparece. */}
            {!full && !disabled && (
              <li className="w-36 shrink-0">
                <span className="relative grid aspect-square w-full place-items-center rounded-borda border border-dashed border-borda-forte text-texto-tenue transition-colors hover:border-primaria-texto hover:text-primaria-texto">
                  <span className="flex flex-col items-center gap-2">
                    <Icon nome="imagem" size={24} />
                    <span className="text-xs">Adicionar anexo</span>
                  </span>
                  <input
                    type="file"
                    multiple
                    accept={accept}
                    aria-label="Adicionar anexo"
                    disabled={disabled || busy}
                    onChange={(e) => {
                      const entry = e.target;
                      const chosen = [...(entry.files ?? [])];
                      entry.value = '';
                      receive(chosen);
                    }}
                    className="absolute inset-0 size-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
                  />
                </span>
              </li>
            )}
          </ul>
        )}
      </div>

      {/*
       * A frase e o contador ficam FORA da moldura, e a frase vem de
       * `acceptedSentence()` — nunca de um literal escrito aqui.
       *
       * O teto é o mesmo de sempre; o que mudou nesta leva foi a LISTA, com
       * DOC, MP4, MOV e MP3 entrando de uma vez. Uma frase copiada à mão para
       * cada tela que anexa continuaria dizendo "PNG, JPG, JPEG, PDF, DOC,
       * DOCX, MP4, MOV" depois de o MP3 entrar, e ninguém descobre: a pessoa
       * que tentou anexar o áudio lê que ele não serve e desiste.
       */}
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-texto-tenue">
        <span className="flex min-w-0 flex-1 items-center gap-1.5">
          <Icon nome="informacao" size={16} className="shrink-0" />
          {restrict?.sentence ?? acceptedSentence()}
        </span>
        <span className={full ? 'font-semibold text-alerta' : undefined}>
          {items.length}/{max}
        </span>
      </div>

      {open !== null && (
        <Viewer
          items={items}
          index={open}
          onIndex={setOpen}
          onClose={() => setOpen(null)}
        />
      )}

      <AttachmentRefusals intake={intake} />
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * O anexo do compositor de resposta — compacto, arrastável e colável
 * ------------------------------------------------------------------------- */

/**
 * O nome do print colado com Ctrl+V: `captura-AAAA-MM-DD-HHMMSS.png`.
 *
 * A área de transferência entrega o print como `image.png` — todo print tem o
 * mesmo nome, e três deles na mesma resposta viram três "image.png" que a
 * pessoa não distingue na conversa. O instante no nome separa os três e ainda
 * diz quando foi tirado.
 */
export function pastedFileName(when: Date, typeMime: string): string {
  const two = (n: number) => String(n).padStart(2, '0');
  const ext =
    typeMime === 'image/jpeg' ? 'jpg' : typeMime === 'image/webp' ? 'webp' : 'png';
  return (
    `captura-${when.getFullYear()}-${two(when.getMonth() + 1)}-${two(when.getDate())}-` +
    `${two(when.getHours())}${two(when.getMinutes())}${two(when.getSeconds())}.${ext}`
  );
}

/** Nome que a área de transferência dá a todo print — e que não diz nada. */
const GENERIC_PASTE_NAMES = new Set([
  'image.png',
  'image.jpg',
  'image.jpeg',
  'image.webp',
  '',
]);

/**
 * Um campo que aceita MENOS que a casa — a despesa da venda, que pede foto ou
 * PDF com teto próprio (02/10/2026).
 *
 * Só estreita: `classifyAttachment` continua valendo antes, e `check` recusa
 * por cima dela. `sentence` troca a frase do rodapé, porque a da casa ("até
 * 20 MB", DOCX, MP4…) prometeria o que esta tela não aceita. Quem escreve a
 * frase é a regra do módulo, ao lado do `check` — as duas mudam juntas.
 */
export interface AttachmentRestriction {
  /** Os tipos (MIME) que o seletor de arquivo oferece — um recorte da lista da casa. */
  accept: readonly string[];
  /** A frase do rodapé, no lugar de `acceptedSentence()`. */
  sentence: string;
  /** A recusa, em pt-BR, ou `null` quando passa. */
  check: (typeMime: string, sizeBytes: number) => string | null;
}

export interface AttachmentIntake {
  /** Confere e entrega a `onAdd` o que passou; o resto vira `refusals`. */
  receive: (files: File[]) => void;
  refusals: string[];
  clearRefusals: () => void;
  /** um arquivo está sendo arrastado por cima — a tela realça a área */
  over: boolean;
  /** para espalhar no elemento que recebe o arquivo arrastado */
  dropHandlers: {
    onDragOver: (e: DragEvent<HTMLElement>) => void;
    onDragLeave: (e: DragEvent<HTMLElement>) => void;
    onDrop: (e: DragEvent<HTMLElement>) => void;
  };
  /** para o campo de texto: Ctrl+V de um print vira anexo */
  onPaste: (e: ClipboardEvent<HTMLElement>) => void;
  full: boolean;
}

/**
 * A recepção de arquivos, num lugar só — escolher, arrastar e colar.
 *
 * Nasceu em 23/09/2026 com o compositor compacto, e o `AttachmentField` passou
 * a usá-la também: a conferência (`classifyAttachment`, o teto de
 * `MAX_ATTACHMENTS` contando o que já entrou NESTA escolha) não pode ter duas
 * cópias, porque a segunda é a que esquece o teto.
 *
 * **Colar só intercepta quando há ARQUIVO** na área de transferência: texto
 * colado continua indo para o campo, como sempre.
 */
export function useAttachmentIntake({
  count,
  onAdd,
  max = MAX_ATTACHMENTS,
  disabled = false,
  restrict,
}: {
  /** quantos já estão escolhidos */
  count: number;
  onAdd: (files: File[], typesMime: string[]) => void;
  max?: number;
  disabled?: boolean;
  restrict?: AttachmentRestriction;
}): AttachmentIntake {
  const [refusals, setRefusals] = useState<string[]>([]);
  const [over, setOver] = useState(false);

  const receive = useCallback(
    (chosen: File[]) => {
      if (disabled || chosen.length === 0) return;

      const accepted: File[] = [];
      const types: string[] = [];
      const refused: string[] = [];

      for (const file of chosen) {
        /*
         * O teto é conferido dentro do laço e conta o que já entrou NESTA
         * escolha: quem arrasta vinte com dois já escolhidos leva dez, e os
         * outros aparecem no aviso dizendo por quê.
         */
        if (count + accepted.length >= max) {
          refused.push(`${file.name} não entrou: o limite é de ${max} anexos.`);
          continue;
        }
        const verdict = classifyAttachment(file.name, file.type, file.size);
        if (!verdict.ok) {
          refused.push(verdict.reason);
          continue;
        }
        const narrower = restrict?.check(verdict.typeMime, file.size);
        if (narrower) {
          refused.push(`${file.name}: ${narrower}`);
          continue;
        }
        accepted.push(file);
        types.push(verdict.typeMime);
      }

      if (accepted.length > 0) onAdd(accepted, types);
      // a recusa de um não cancela os outros
      setRefusals(refused);
    },
    [count, max, onAdd, disabled, restrict],
  );

  return {
    receive,
    refusals,
    clearRefusals: () => setRefusals([]),
    over,
    full: count >= max,
    dropHandlers: {
      onDragOver: (e) => {
        // só arquivo de fora: arrastar um ladrilho não acende a área
        if (disabled || !e.dataTransfer.types.includes('Files')) return;
        e.preventDefault();
        setOver(true);
      },
      onDragLeave: (e) => {
        // sair para um FILHO dispara dragleave; só apaga ao sair de verdade
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        setOver(false);
      },
      onDrop: (e) => {
        if (!e.dataTransfer.types.includes('Files')) return;
        e.preventDefault();
        setOver(false);
        receive([...e.dataTransfer.files]);
      },
    },
    onPaste: (e) => {
      const files = [...e.clipboardData.files];
      if (files.length === 0) return;
      e.preventDefault();
      const now = new Date();
      receive(
        files.map((f) =>
          GENERIC_PASTE_NAMES.has(f.name)
            ? new File([f], pastedFileName(now, f.type), { type: f.type })
            : f,
        ),
      );
    },
  };
}

/**
 * O clipe do compositor: um botão pequeno, na barra ao lado de "Enviar".
 *
 * O `<input type="file">` fica POR CIMA do botão, como no `AttachmentField`:
 * clique, toque, Tab e Enter são os nativos. O rótulo "Anexar arquivo" é o que
 * os percursos no navegador procuram (`/anexar/i`). O `title` lembra os dois
 * outros caminhos — arrastar e colar —, que não têm botão.
 */
export function AttachmentClip({
  intake,
  disabled = false,
  busy = false,
}: {
  intake: AttachmentIntake;
  disabled?: boolean;
  busy?: boolean;
}) {
  return (
    <span
      title="Anexar arquivo — você também pode arrastar para cá ou colar um print (Ctrl+V)"
      className={buttonClasses({
        variant: 'discreto',
        size: 'sm',
        className: 'relative',
      })}
    >
      <input
        type="file"
        multiple
        accept={acceptAttribute()}
        aria-label="Anexar arquivo"
        aria-busy={busy || undefined}
        disabled={disabled || busy || intake.full}
        onChange={(e) => {
          const entry = e.target;
          const chosen = [...(entry.files ?? [])];
          // sem isto, escolher o MESMO arquivo de novo não dispara `change`
          entry.value = '';
          intake.receive(chosen);
        }}
        className="absolute inset-0 size-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
      />
      <Icon nome="clipe" size={16} />
      {busy ? 'Lendo…' : 'Anexar'}
    </span>
  );
}

/**
 * Os arquivos escolhidos, em ladrilhos pequenos (80px) acima da barra do
 * compositor — com a prévia e o remover. Some quando não há nenhum: o
 * compositor vazio não gasta altura com uma área de arquivo.
 */
export function AttachmentStrip({
  items,
  onRemove,
  disabled = false,
  max = MAX_ATTACHMENTS,
}: {
  items: AttachmentItem[];
  onRemove: (key: string) => void;
  disabled?: boolean;
  max?: number;
}) {
  const [open, setOpen] = useState<number | null>(null);
  if (items.length === 0) return null;

  return (
    <div>
      <ul className="flex flex-wrap items-start gap-3">
        {items.map((item, i) => (
          <Tile
            key={item.key}
            item={item}
            compact
            disabled={disabled}
            onOpen={() => setOpen(i)}
            onRemove={disabled ? undefined : () => onRemove(item.key)}
          />
        ))}
      </ul>
      <p className="mt-1.5 text-xs text-texto-tenue">
        {items.length}/{max} · {acceptedSentence()}
      </p>
      {open !== null && (
        <Viewer
          items={items}
          index={open}
          onIndex={setOpen}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}

/** O aviso "Formato não aceito", com o nome de cada arquivo e o motivo. */
export function AttachmentRefusals({ intake }: { intake: AttachmentIntake }) {
  const { refusals } = intake;
  return (
    <Modal
      open={refusals.length > 0}
      onOpenChange={(isOpen) => !isOpen && intake.clearRefusals()}
      tone="destaque"
      icon="atencao"
      title="Formato não aceito"
    >
      <p className="text-sm">
        {refusals.length === 1
          ? 'Este arquivo não entrou:'
          : 'Estes arquivos não entraram:'}
      </p>
      <ul className="mt-2 flex list-disc flex-col gap-1 pl-5 text-sm text-texto-suave">
        {refusals.map((reason) => (
          <li key={reason}>{reason}</li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-texto-tenue">
        A casa aceita {acceptedLabels()}, até {formatBytes(MAX_BYTES)} por arquivo.
      </p>
      <div className="mt-4 flex justify-end">
        <Button icon="confirmado" onClick={() => intake.clearRefusals()}>
          Entendi
        </Button>
      </div>
    </Modal>
  );
}
